import type React from "react";
import {
  Image,
  type ImageSourcePropType,
  Pressable,
  Text,
  View,
} from "react-native";
import { Check, ChevronDown, ChevronUp } from "lucide-react-native";
import { LinearGradient } from "expo-linear-gradient";
import { Collapsible } from "@louise/moon-ui";
import { EMBER, GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { NeutralStepPill } from "./NeutralStepPill";
import { useTaskSectionState } from "../hooks/useTaskSectionState";

type Props = {
  id: string;
  stepLabel: string;
  completedStepLabel?: string;
  title: string;
  subtitle?: string;
  defaultExpanded?: boolean;
  collapsedPreviewHeight?: number;
  children: React.ReactNode;
};

export function TaskSectionCard({
  id,
  stepLabel,
  completedStepLabel,
  title,
  subtitle,
  defaultExpanded = true,
  collapsedPreviewHeight = 56,
  children,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("TaskSectionCard");
  const { completed, expanded, toggleCompleted, toggleExpanded } =
    useTaskSectionState(id, { defaultExpanded });

  return (
    <View style={{ marginTop: 16, marginBottom: 16 }}>
      <View style={{ paddingHorizontal: 2, marginBottom: 8 }}>
        <NeutralStepPill
          label={completed ? (completedStepLabel ?? stepLabel) : stepLabel}
        />
      </View>

      <View
        style={{
          ...(GLASS_CARD_STYLE as Record<string, unknown>),
          borderRadius: 20,
          padding: 18,
          overflow: "hidden",
          ...(completed ? { opacity: 0.84 } : null),
        }}
      >
        <Pressable
          onPress={() => {
            capturePress(pressLabel(title), "task_section_header");
            toggleExpanded();
          }}
          accessibilityRole="button"
          accessibilityLabel={
            expanded
              ? t("homepage.task_section_card.collapse_title", {
                  params: { title },
                })
              : t("homepage.task_section_card.expand_title", {
                  params: { title },
                })
          }
          accessibilityState={{ expanded }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <Pressable
              onPress={(event) => {
                event.stopPropagation();
                capturePress(
                  pressLabel(title),
                  "task_section_completion_toggle",
                );
                toggleCompleted();
              }}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: completed }}
              accessibilityLabel={
                completed
                  ? t("homepage.task_section_card.mark_title_incomplete", {
                      params: { title },
                    })
                  : t("homepage.task_section_card.mark_title_done", {
                      params: { title },
                    })
              }
              hitSlop={10}
              style={{
                width: 22,
                height: 22,
                borderRadius: 999,
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: completed
                  ? EMBER.colors.green
                  : "rgba(255,255,255,0.32)",
                borderWidth: completed ? 0 : 2,
                borderColor: "rgba(92, 52, 32, 0.18)",
              }}
            >
              {completed ? (
                <Check size={14} color="#FFFFFF" strokeWidth={3} />
              ) : (
                <Check
                  size={13}
                  color="rgba(107, 85, 72, 0.16)"
                  strokeWidth={2}
                />
              )}
            </Pressable>

            <View style={{ flex: 1 }}>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 18,
                  lineHeight: 23,
                  color: completed ? EMBER.colors.textLight : EMBER.colors.text,
                  fontWeight: "700",
                }}
              >
                {title}
              </Text>
              {subtitle ? (
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 13,
                    lineHeight: 18,
                    color: EMBER.colors.textLight,
                    fontWeight: "400",
                    marginTop: 3,
                  }}
                >
                  {subtitle}
                </Text>
              ) : null}
            </View>

            <View
              style={{
                width: 22,
                height: 22,
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {expanded ? (
                <ChevronUp
                  size={18}
                  color={EMBER.colors.textLight}
                  strokeWidth={1.6}
                />
              ) : (
                <ChevronDown
                  size={18}
                  color={EMBER.colors.textLight}
                  strokeWidth={1.6}
                />
              )}
            </View>
          </View>
        </Pressable>

        {!expanded ? (
          <Pressable
            onPress={() => {
              capturePress(pressLabel(title), "task_section_preview");
              toggleExpanded();
            }}
            accessibilityRole="button"
            accessibilityLabel={t("homepage.task_section_card.expand_title", {
              params: { title },
            })}
            style={{
              marginTop: 0,
              height: collapsedPreviewHeight,
              overflow: "hidden",
              opacity: 0.5,
            }}
          >
            <View pointerEvents="none" style={{ paddingTop: 14 }}>
              {children}
            </View>
            <LinearGradient
              colors={[
                "rgba(248,244,238,0)",
                "rgba(248,244,238,0.72)",
                "rgba(248,244,238,0.98)",
              ]}
              pointerEvents="none"
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: collapsedPreviewHeight,
              }}
            />
          </Pressable>
        ) : null}

        <Collapsible expanded={expanded}>
          <View style={{ paddingTop: 14 }}>{children}</View>
        </Collapsible>
      </View>
    </View>
  );
}

export function InlineTaskSectionCard({
  id,
  stepLabel,
  title,
  subtitle,
  titleIcon,
  titleImage,
  defaultExpanded = true,
  collapsedPreviewHeight = 56,
  children,
}: Props & { titleIcon?: React.ReactNode; titleImage?: ImageSourcePropType }) {
  const t = useTranslations();
  const capturePress = usePressCapture("InlineTaskSectionCard");
  const { completed, expanded, toggleCompleted, toggleExpanded } =
    useTaskSectionState(id, { defaultExpanded });

  return (
    <View style={{ marginTop: 16, marginBottom: 16 }}>
      <View
        style={{
          ...(GLASS_CARD_STYLE as Record<string, unknown>),
          borderRadius: 20,
          padding: 18,
          overflow: "hidden",
          ...(completed ? { opacity: 0.84 } : null),
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
            <Pressable
              onPress={() => {
                capturePress(
                  pressLabel(title),
                  "task_section_completion_toggle",
                );
                toggleCompleted();
              }}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: completed }}
              accessibilityLabel={
                completed
                  ? t("homepage.task_section_card.mark_title_incomplete", {
                      params: { title },
                    })
                  : t("homepage.task_section_card.mark_title_done", {
                      params: { title },
                    })
              }
              hitSlop={10}
              style={{
                width: 22,
                height: 22,
                borderRadius: 999,
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: completed
                  ? EMBER.colors.green
                  : "rgba(255,255,255,0.32)",
                borderWidth: completed ? 0 : 2,
                borderColor: "rgba(92, 52, 32, 0.18)",
              }}
            >
              {completed ? (
                <Check size={14} color="#FFFFFF" strokeWidth={3} />
              ) : (
                <Check
                  size={13}
                  color="rgba(107, 85, 72, 0.16)"
                  strokeWidth={2}
                />
              )}
            </Pressable>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: "600",
                color: EMBER.colors.textLight,
                letterSpacing: 1,
                textTransform: "uppercase",
                textDecorationLine: completed ? "line-through" : "none",
              }}
            >
              {stepLabel}
            </Text>
          </View>
        </View>

        <Pressable
          onPress={() => {
            capturePress(pressLabel(title), "task_section_header");
            toggleExpanded();
          }}
          accessibilityRole="button"
          accessibilityLabel={
            expanded
              ? t("homepage.task_section_card.collapse_title", {
                  params: { title },
                })
              : t("homepage.task_section_card.expand_title", {
                  params: { title },
                })
          }
          accessibilityState={{ expanded }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <View
              style={{
                flex: 1,
                flexDirection: "row",
                alignItems: "center",
                gap: 8,
              }}
            >
              {titleIcon ? (
                <View
                  style={{
                    width: 36,
                    height: 42,
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {titleIcon}
                </View>
              ) : titleImage ? (
                <Image
                  source={titleImage}
                  resizeMode="contain"
                  style={{
                    width: 24,
                    height: 42,
                    flexShrink: 0,
                  }}
                />
              ) : null}
              <View style={{ flex: 1 }}>
                <Text
                  className="font-dm-sans"
                  style={{
                    fontSize: 18,
                    lineHeight: 23,
                    color: completed
                      ? EMBER.colors.textLight
                      : EMBER.colors.text,
                    fontWeight: "700",
                  }}
                >
                  {title}
                </Text>
                {subtitle ? (
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 13,
                      lineHeight: 18,
                      color: EMBER.colors.textLight,
                      fontWeight: "400",
                      marginTop: 3,
                    }}
                  >
                    {subtitle}
                  </Text>
                ) : null}
              </View>
            </View>
            <View
              style={{
                width: 22,
                height: 22,
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {expanded ? (
                <ChevronUp
                  size={18}
                  color={EMBER.colors.textLight}
                  strokeWidth={1.6}
                />
              ) : (
                <ChevronDown
                  size={18}
                  color={EMBER.colors.textLight}
                  strokeWidth={1.6}
                />
              )}
            </View>
          </View>
        </Pressable>

        {!expanded ? (
          <Pressable
            onPress={() => {
              capturePress(pressLabel(title), "task_section_preview");
              toggleExpanded();
            }}
            accessibilityRole="button"
            accessibilityLabel={t("homepage.task_section_card.expand_title", {
              params: { title },
            })}
            style={{
              marginTop: 0,
              height: collapsedPreviewHeight,
              overflow: "hidden",
              opacity: 0.5,
            }}
          >
            <View pointerEvents="none" style={{ paddingTop: 14 }}>
              {children}
            </View>
            <LinearGradient
              colors={[
                "rgba(248,244,238,0)",
                "rgba(248,244,238,0.72)",
                "rgba(248,244,238,0.98)",
              ]}
              pointerEvents="none"
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: collapsedPreviewHeight,
              }}
            />
          </Pressable>
        ) : null}

        <Collapsible expanded={expanded}>
          <View style={{ paddingTop: 14 }}>{children}</View>
        </Collapsible>
      </View>
    </View>
  );
}
